import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Card, CardContent, IconButton, Menu, MenuItem, Divider } from '@mui/material';
import { 
  Assessment as AssessmentIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { signOut } from 'firebase/auth';
import { AUTH_LOGIN } from '../../../Config/firebase';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

// Components
import FundSummaryCards from './components/FundSummaryCards';
import PerformanceTimeline from './components/PerformanceTimeline';
import MonthlyBreakdownTable from './components/MonthlyBreakdownTable';
import ClientAnalytics from './components/ClientAnalytics';
import DataIntegrityChecker from './components/DataIntegrityChecker';
import DataComparisonTool from './components/DataComparisonTool';

// Services
import { getFundSummary, getMonthlyHistory, getClientAnalyticsLive } from '../../../services/fundHistoryService';

const FundControlCenter = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [clientData, setClientData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const user_info = JSON.parse(localStorage.getItem('fiddux_info')) || null;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);

    try {
      // Load all data in parallel
      const [summaryResponse, monthlyResponse, clientResponse] = await Promise.all([
        getFundSummary(),
        getMonthlyHistory(),
        getClientAnalyticsLive()
      ]);

      if (summaryResponse.success) {
        setSummary(summaryResponse.data);
      } else {
        console.error('Error loading summary:', summaryResponse.error);
        Swal.fire({
          title: 'Data Not Found',
          text: 'Fund history data has not been imported yet. Please run the import script first.',
          icon: 'warning',
          confirmButtonColor: '#46adf2'
        });
      }

      if (monthlyResponse.success) {
        // Transform data for timeline
        const timeline = monthlyResponse.data.map(month => ({
          month: month.month,
          year: month.year,
          displayName: `${dayjs().month(month.month).format('MMM')} ${month.year}`,
          deposits: month.aggregates.totalDeposits,
          withdrawals: month.aggregates.totalWithdrawals,
          netPerformance: month.aggregates.totalNetPerformance,
          aum: month.aggregates.totalAUM,
          activeClients: month.aggregates.activeClients,
          returnPercent: month.aggregates.totalAUM > 0
            ? (month.aggregates.totalNetPerformance / month.aggregates.totalAUM) * 100
            : 0
        }));
        setMonthlyData(timeline);
      } else {
        console.error('Error loading monthly data:', monthlyResponse.error);
      }

      if (clientResponse.success) {
        setClientData(clientResponse.data);
      } else {
        console.error('Error loading client data:', clientResponse.error);
        Swal.fire({
          title: 'Error Loading Client Data',
          text: clientResponse.error || 'Could not load users, ledgers, or transactions from Firestore.',
          icon: 'warning',
          confirmButtonColor: '#46adf2'
        });
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading data:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load fund control center data',
        icon: 'error',
        confirmButtonColor: '#46adf2'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogoutClick = () => {
    setAnchorEl(null);
    Swal.fire({
      title: 'Are you sure you want to logout?',
      text: 'Logging out will end your session and you will be redirected to the login page.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#46adf2',
      cancelButtonColor: '#5b5d5d',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await signOut(AUTH_LOGIN);
        localStorage.clear();
        Swal.fire({
          title: 'Logged Out',
          text: 'You have been successfully logged out. Redirecting to login page...',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
        }).then(() => {
          navigate('/login');
        });
      }
    });
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          padding: '20px'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '20px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon style={{ fontSize: '32px', color: '#2af598' }} />
            <Box>
              <Typography sx={{ fontSize: '24px', fontWeight: '700', color: '#fff' }}>
                Fund Control Center
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#aaa' }}>
                Complete fund analytics and business intelligence
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {lastUpdated && (
              <Typography sx={{ fontSize: '12px', color: '#aaa' }}>
                Last updated: {dayjs(lastUpdated).format('MMM DD, YYYY HH:mm')}
              </Typography>
            )}
            
            <IconButton
              onClick={loadData}
              disabled={isLoading}
              sx={{
                width: '40px',
                height: '40px',
                backgroundColor: 'rgba(42, 245, 152, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(42, 245, 152, 0.2)'
                }
              }}
            >
              <RefreshIcon sx={{ color: '#2af598' }} />
            </IconButton>

            <IconButton
              onClick={handleMenuClick}
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'rgba(42, 245, 152, 0.1)',
                color: '#2af598'
              }}
            >
              <MoreVertIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  backgroundColor: '#2a2a2a',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                }
              }}
            >
              <MenuItem
                onClick={handleMenuClose}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(42, 245, 152, 0.1)'
                  }
                }}
              >
                <Typography sx={{ fontSize: '14px' }}>
                  {user_info?.email || 'Admin'}
                </Typography>
              </MenuItem>
              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
              <MenuItem
                onClick={handleLogoutClick}
                sx={{
                  color: '#f52a2d',
                  '&:hover': {
                    backgroundColor: 'rgba(245, 42, 45, 0.1)'
                  }
                }}
              >
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Summary Cards */}
        <Box sx={{ mb: 3 }}>
          <FundSummaryCards summary={summary} isLoading={isLoading} />
        </Box>

        {/* Performance Timeline */}
        <Box sx={{ mb: 3 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#fff', mb: 2 }}>
                Performance Timeline
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#ccc', mb: 3 }}>
                Monthly trends showing deposits, withdrawals, performance, and AUM growth
              </Typography>
              <PerformanceTimeline monthlyData={monthlyData} isLoading={isLoading} />
            </CardContent>
          </Card>
        </Box>

        {/* Monthly Breakdown Table */}
        <Box sx={{ mb: 3 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#fff', mb: 2 }}>
                Monthly Breakdown
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#ccc', mb: 3 }}>
                Detailed month-by-month performance analysis
              </Typography>
              <MonthlyBreakdownTable monthlyData={monthlyData} isLoading={isLoading} />
            </CardContent>
          </Card>
        </Box>

        {/* Client Analytics */}
        <Box sx={{ mb: 3 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#fff', mb: 2 }}>
                Client Analytics
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#ccc', mb: 3 }}>
                Top performers and complete client breakdown
              </Typography>
              <ClientAnalytics clientData={clientData} isLoading={isLoading} />
            </CardContent>
          </Card>
        </Box>

        {/* Data Comparison Tool */}
        <Box sx={{ mb: 3 }}>
          <DataComparisonTool />
        </Box>

        {/* Data Integrity Checker */}
        <Box sx={{ mb: 3 }}>
          <DataIntegrityChecker />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default FundControlCenter;

